import { useQuery } from '@tanstack/react-query';
import { PropsWithChildren, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  EventMapContextProps,
  EventMapContextWithSelector,
} from 'src/contexts/EventMapContext/EventMapContext';
import { EventConfigClient } from 'src/WebApiController';

/**
 * A minimal EventMapContext provider that loads the venue map from a venueConfigId
 * instead of an event. Intended for use in the placeholder event group creation flow
 * where no real event exists yet.
 */
export const PlaceholderEventMapContextProvider = ({
  venueConfigId,
  children,
}: PropsWithChildren<{ venueConfigId: number | null }>) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const shouldQuery =
    venueConfigId != null &&
    activeAccountWebClientConfig.activeAccountId != null;

  const venueMapQuery = useQuery({
    queryKey: [
      'EventConfigClient.getVenueMapByVenueConfig',
      activeAccountWebClientConfig.activeAccountId,
      venueConfigId,
    ],
    queryFn: async () => {
      if (!shouldQuery) {
        return null;
      }
      return await new EventConfigClient(
        activeAccountWebClientConfig
      ).getVenueMapByVenueConfig(venueConfigId!);
    },
    enabled: shouldQuery,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    meta: {
      persist: true,
      onError: (error: ErrorTypes) => {
        trackError('EventConfigClient.getVenueMapByVenueConfig', error, {
          venueConfigId,
        });
      },
    },
  });

  const venueMapInfo = venueMapQuery.data ?? null;

  const ticketClasses = useMemo(() => {
    if (!venueMapInfo) return [];
    return Object.values(
      venueMapInfo.sections
        .flatMap((s) => s.rows.map((r) => r.tktClass))
        .reduce(
          (r, c) => {
            if (c?.id) {
              r[c.id] = c;
            }
            return r;
          },
          {} as Record<
            number,
            NonNullable<
              (typeof venueMapInfo.sections)[0]['rows'][0]['tktClass']
            >
          >
        )
    );
  }, [venueMapInfo]);

  const contextValue = useMemo(
    (): EventMapContextProps => ({
      event: null,
      ticketClasses,
      venueMapQuery: venueMapQuery as EventMapContextProps['venueMapQuery'],
      venueMapsByScoreModelQuery:
        {} as EventMapContextProps['venueMapsByScoreModelQuery'],
      isMapLoading: venueMapQuery.isLoading,
      venueMapInfo,
      mapConfigOverridesQuery:
        {} as EventMapContextProps['mapConfigOverridesQuery'],
      sellerAccountScoreOverridesQuery:
        {} as EventMapContextProps['sellerAccountScoreOverridesQuery'],
      activeConfigOverride: null,
      isGeneralAdmissionOnly: false,
      normalizeConfigOverrideWithUiScore: (x) => x,
      venueZoneConfigs: null,
    }),
    [ticketClasses, venueMapInfo, venueMapQuery]
  );

  return (
    <EventMapContextWithSelector.Provider value={contextValue}>
      {children}
    </EventMapContextWithSelector.Provider>
  );
};
